import React, { Component } from 'react'
import { Button, Col, DatePicker, Input, Row, Select, Tooltip, message } from 'antd'
import {
    CLEARING_OPTIONS,
    DOCUMENT_TERMINALS,
    DOCUMENT_TYPE_OPTIONS,
    PRODUCT_MAX_VALUES,
    SKU_MAX_LENGTH,
    SKU_MAX_VALUES,
    normalizeProductSelection,
    normalizeSkuSelection,
    productOptionLabels,
    pruneSkuSelection,
    resolveSkuInput,
    skuOptionsFor
} from '../utils'
import * as styles from './style.scss'

// Same visual language as Tranzila/SearchBar (antd Row/Col grid, label above control, explicit
// Search/Reset buttons) -- but NOT its architecture: every filter here is sent to the server.
// Nothing is filtered in the browser.

const ROW = { type: 'flex' as 'flex', gutter: 16, align: 'bottom' as 'bottom', style: { rowGap: '0.8rem' } }
const COL = { xs: 24, sm: 12, md: 8, lg: 6, xl: 4, xxl: 4 }
const LABEL_STYLE = {
    display: 'block',
    marginBottom: '0.35rem',
    fontSize: '0.85rem',
    lineHeight: '1.2rem',
    whiteSpace: 'nowrap' as 'nowrap'
}
const CONTROL_STYLE = { width: '100%', border: 'none' }
// The COL spans above are viewport-based, so once the side menu is open the xl/xxl 4-span column
// is narrower than the two buttons and they wrapped onto two rows. The action column is sized by
// its content instead (bare Col = content width in a flex Row), and styles.actions keeps the pair
// on one line at every width. RTL puts the first child on the right, i.e. the existing
// איפוס | חיפוש order.

// Declared once so adding or reordering a text filter in V2 is a one-line edit here. The list is
// SPLIT around the SKU slot rather than ending at it: SKU is its own control now, but it keeps the
// position it always had, between מס׳ עסקה and סכום.
const TEXT_FILTERS_BEFORE_SKU = [
    { key: 'user_id', label: 'מזהה לקוח', placeholder: 'לדוגמה: 109852' },
    { key: 'client', label: 'לקוח', placeholder: 'שם או טקסט מזהה' },
    { key: 'email', label: 'אימייל', placeholder: 'מלא או חלקי' },
    { key: 'document_number', label: 'מספר מסמך', placeholder: 'חשבונית או זיכוי' },
    { key: 'transaction_index', label: 'מס׳ עסקה', placeholder: 'לדוגמה: 8548' }
]

const TEXT_FILTERS_AFTER_SKU = [{ key: 'amount', label: 'סכום (₪)', placeholder: 'לדוגמה: 267 או 267.50' }]

// The label carries a double quote, so it lives in a constant rather than in a JSX attribute.
const SKU_LABEL = 'מק"ט'
const PRODUCT_LABEL = 'מוצר'

// The מוצר / מק"ט panels are wider than their triggers (see .wideDropdown). antd 3's bottomLeft
// placement ships with adjustX: 0, so a wide panel anchored to a trigger near the edge ran past
// a phone viewport; letting it adjust horizontally keeps it on screen. Merged over antd's own
// placement, so its points and offset are unchanged.
const WIDE_DROPDOWN_ALIGN = { overflow: { adjustX: 1, adjustY: 1 } }

// Same RTL dropdown treatment the cancellation-requests filters use.
const RTL_DROPDOWN = { direction: 'rtl', textAlign: 'right' } as React.CSSProperties

const Field = ({ label, children }) => (
    <Col {...COL}>
        <label style={LABEL_STYLE}>{label}</label>
        {children}
    </Col>
)

export default class DocumentsSearchBar extends Component<any, any> {
    // One rendering for every scalar text filter, so splitting the list around the SKU slot costs
    // a position in the array and not a second copy of the Input.
    renderTextFilter = filter => {
        const { filters, setFilter, onSearch } = this.props
        return (
            <Field key={filter.key} label={filter.label}>
                <Input
                    style={CONTROL_STYLE}
                    placeholder={filter.placeholder}
                    value={filters[filter.key]}
                    onChange={event => setFilter(filter.key, event.target.value)}
                    onPressEnter={onSearch}
                />
            </Field>
        )
    }

    // The refusals are explained here rather than swallowed: a dropped tag with no reason is
    // indistinguishable from a broken control.
    handleSkuChange = values => {
        const { filters, productOptions = [], skuOptions = [] } = this.props
        // a subscription NAME typed and confirmed with Enter becomes its SKU, never a "SKU" itself
        const typed = resolveSkuInput(values, skuOptionsFor(skuOptions, productOptions, filters.product_id))
        if (typed.ambiguous.length > 0) {
            message.warning(`נמצאו כמה מק"טים עבור: ${typed.ambiguous.join(', ')} — יש לבחור מהרשימה`)
        }
        if (typed.unmatched.length > 0) message.warning(`לא נמצא מק"ט עבור: ${typed.unmatched.join(', ')}`)
        const selection = normalizeSkuSelection(typed.values)
        if (selection.tooLong.length > 0) {
            message.warning(`מק"ט יכול להכיל עד ${SKU_MAX_LENGTH} תווים: ${selection.tooLong.join(', ')}`)
        }
        if (selection.overflow) message.warning(`ניתן לבחור עד ${SKU_MAX_VALUES} מק"טים`)
        // With products selected the list is theirs alone, and a typed code outside it could only
        // ever match nothing -- so it is refused here, visibly, instead of becoming a hidden
        // impossible filter. With no product selected, free entry stays exactly as it was.
        const allowed = pruneSkuSelection(selection.values, productOptions, filters.product_id)
        const refused = selection.values.filter(sku => allowed.indexOf(sku) === -1)
        if (refused.length > 0) message.warning(`המק"ט אינו שייך למוצרים שנבחרו: ${refused.join(', ')}`)
        this.props.setFilter('sku', allowed)
    }

    // The draft product selection drives the מק"ט list at once (no Search needed, no request
    // sent). A selected SKU that no longer belongs to ANY selected product is dropped from the
    // draft; clearing every product keeps them all, because the list is global again.
    handleProductChange = values => {
        const { filters, productOptions = [] } = this.props
        const selection = normalizeProductSelection(values)
        if (selection.overflow) message.warning(`ניתן לבחור עד ${PRODUCT_MAX_VALUES} מוצרים`)
        this.props.setFilter('product_id', selection.values)
        const current = Array.isArray(filters.sku) ? filters.sku : []
        const kept = pruneSkuSelection(current, productOptions, selection.values)
        if (kept.length !== current.length) this.props.setFilter('sku', kept)
    }

    // Search by what the operator reads: the label, inactive suffix included.
    filterProduct = (input, option) =>
        String(option.props.children || '')
            .toLowerCase()
            .indexOf(String(input || '').toLowerCase()) !== -1

    render() {
        const { filters, setFilter, onSearch, onReset, loading, skuOptions = [], productOptions = [] } = this.props
        const productLabels = productOptionLabels(productOptions)
        const skuChoices = skuOptionsFor(skuOptions, productOptions, filters.product_id)
        // Typing in מק"ט matches the code AND the subscription names, through this lookup -- not
        // through whatever the rendered option happens to contain.
        const skuSearch = {}
        for (const choice of skuChoices) skuSearch[choice.value] = choice.search
        const filterSku = (input, option) =>
            (skuSearch[option.props.value] || String(option.props.value || '').toLowerCase()).indexOf(
                String(input || '')
                    .trim()
                    .toLowerCase()
            ) !== -1

        return (
            <div className="box" style={{ padding: '1rem', marginBottom: '1rem' }}>
                <Row {...ROW}>
                    <Field label="מתאריך">
                        <DatePicker
                            style={CONTROL_STYLE}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            value={filters.date_from}
                            onChange={value => setFilter('date_from', value)}
                        />
                    </Field>
                    <Field label="עד תאריך">
                        <DatePicker
                            style={CONTROL_STYLE}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            value={filters.date_to}
                            onChange={value => setFilter('date_to', value)}
                        />
                    </Field>

                    {TEXT_FILTERS_BEFORE_SKU.map(filter => this.renderTextFilter(filter))}

                    {/* מוצר sits directly before מק"ט: the business-level choice first, the exact
                        code next to it. mode="multiple", NOT "tags": only a real product can be
                        chosen, its id is what is sent, and the API resolves it to SKUs. Selecting
                        several means ANY of them; combined with מק"ט it means BOTH. */}
                    <Field label={PRODUCT_LABEL}>
                        <Select
                            mode="multiple"
                            showSearch
                            allowClear
                            maxTagCount={1}
                            style={CONTROL_STYLE}
                            placeholder="כל המוצרים"
                            notFoundContent="לא נמצא מוצר"
                            dropdownStyle={RTL_DROPDOWN}
                            dropdownMatchSelectWidth={false}
                            dropdownClassName={styles.wideDropdown}
                            dropdownAlign={WIDE_DROPDOWN_ALIGN}
                            filterOption={this.filterProduct}
                            value={filters.product_id || []}
                            onChange={values => this.handleProductChange(values)}
                        >
                            {productOptions.map(option => (
                                <Select.Option key={option.id} value={option.id} title={productLabels[option.id]}>
                                    {productLabels[option.id]}
                                </Select.Option>
                            ))}
                        </Select>
                    </Field>

                    {/* mode="tags", not "multiple": an operator must be able to filter by a code
                        that exists in neither table yet. No tokenSeparators -- there is no rule
                        saying a SKU cannot contain a space or a comma, so nothing is split for
                        them; Enter adds a value. Values are uppercased before they reach state. */}
                    <Field label={SKU_LABEL}>
                        <Select
                            mode="tags"
                            style={CONTROL_STYLE}
                            maxTagCount={1}
                            allowClear
                            placeholder="לדוגמה: B-0014"
                            dropdownStyle={RTL_DROPDOWN}
                            dropdownMatchSelectWidth={false}
                            dropdownClassName={styles.wideDropdown}
                            dropdownAlign={WIDE_DROPDOWN_ALIGN}
                            // the dropdown reads "SKU — subscription"; the chosen tag is the SKU alone
                            optionLabelProp="value"
                            filterOption={filterSku}
                            value={filters.sku}
                            onChange={values => this.handleSkuChange(values)}
                        >
                            {skuChoices.map(choice => (
                                <Select.Option key={choice.value} value={choice.value} title={choice.label}>
                                    {choice.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Field>

                    {TEXT_FILTERS_AFTER_SKU.map(filter => this.renderTextFilter(filter))}

                    <Field label="מסוף">
                        <Select
                            style={CONTROL_STYLE}
                            allowClear
                            placeholder="הכל"
                            value={filters.terminal || undefined}
                            onChange={value => setFilter('terminal', value || '')}
                        >
                            {DOCUMENT_TERMINALS.map(terminal => (
                                <Select.Option key={terminal} value={terminal}>
                                    {terminal}
                                </Select.Option>
                            ))}
                        </Select>
                    </Field>

                    <Field label="סוג מסמך">
                        <Select
                            style={CONTROL_STYLE}
                            value={filters.document_type}
                            onChange={value => setFilter('document_type', value)}
                        >
                            {DOCUMENT_TYPE_OPTIONS.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Field>

                    <Field label="סולק">
                        <Select
                            style={CONTROL_STYLE}
                            allowClear
                            placeholder="הכל"
                            value={filters.clearing_processor || undefined}
                            onChange={value => setFilter('clearing_processor', value || '')}
                        >
                            {CLEARING_OPTIONS.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Field>

                    <Col className={styles.actionsColumn}>
                        <label style={LABEL_STYLE}>&nbsp;</label>
                        <div className={styles.actions}>
                            <Tooltip title="החיפוש מתבצע על המטמון המקומי, ללא פנייה לטרנזילה">
                                <Button type="primary" icon="search" loading={loading} onClick={onSearch}>
                                    חיפוש
                                </Button>
                            </Tooltip>
                            <Button icon="reload" disabled={loading} onClick={onReset}>
                                איפוס
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
        )
    }
}
